import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import { CrudResource, ListQuery } from './defineResource';
import { compileFilter, OPERATORS_BY_TYPE } from './filterTypes';
import { TenantParams, useCrudConfig } from './provider';
import { CrudSort, FilterState } from './urlState';
import { CrudFiltersState, useCrudFilters } from './useCrudFilters';
import { REFERENCE_QUERY_PREFIX } from './useReferenceOptions';
import { warnInDev } from './warnInDev';

/**
 * How long a fetched list stays fresh. A mutation marks the entries it orphans stale immediately;
 * this only bounds how long a list may serve cache to a user who keeps navigating without saving.
 * Finite rather than Infinity so window-focus refetch still works for lists.
 */
const LIST_STALE_TIME_MS = 60_000;

export type UseCrudResourceResult<TEntity, TCreate, TUpdate> = CrudFiltersState & {
  rows: TEntity[];
  total: number;
  isLoading: boolean;
  isRefetching: boolean;
  refetch: () => void;
  create: ReturnType<typeof useMutation<unknown, unknown, TCreate>>;
  update: ReturnType<typeof useMutation<unknown, unknown, { id: string } & TUpdate>>;
  remove: ReturnType<typeof useMutation<unknown, unknown, string>>;
  submitting: boolean;
  saveError: string | null;
  deleteError: string | null;
  params: TenantParams;
  /**
   * The narrowing the list request carries, minus paging. A view that fans this out into its
   * own requests (the kanban board issues one per lane) reuses it so both views agree on what
   * the toolbar, filters and search currently mean.
   */
  compiledQuery: Omit<ListQuery, 'page' | 'limit'>;
};

export interface UseCrudResourceOptions {
  /**
   * The sortable column keys of the page's columns. A `sort` arriving from the URL whose
   * field is not here is dropped from the outgoing query (degrading to the server's default
   * order) instead of 400-ing against the server; the value itself stays in the URL,
   * harmlessly. Omit it and every sort passes, unchanged.
   */
  sortableKeys?: string[];
  /**
   * Whether to fetch the list at all. Default true. A page showing a non-table view fetches
   * its own data and would otherwise fire this paged request alongside, doubling traffic on a
   * screen that is already issuing one request per lane.
   */
  enabled?: boolean;
}

export function useCrudResource<TEntity, TCreate, TUpdate, TScope extends TenantParams>(
  resource: CrudResource<TEntity, TCreate, TUpdate, TScope>,
  options: UseCrudResourceOptions = {}
): UseCrudResourceResult<TEntity, TCreate, TUpdate> {
  const { useTenantParams, resolveErrorMessage, dateBound } = useCrudConfig();
  const params = useTenantParams();
  const queryClient = useQueryClient();
  const filters = useCrudFilters({
    debounceMs: resource.debounceMs,
    pageSize: resource.pageSize,
    urlKeyPrefix: resource.urlKeyPrefix,
  });

  const pageSize = filters.pageSize;
  const searchMode = resource.search ?? 'server';
  // The generated request functions declare their own scope type; the provider supplies a
  // superset of it. This is the single place the two meet, so it is the single cast.
  const scope = params as TScope;
  const warnedSortRef = useRef<string | null>(null);
  const warnedFilterRef = useRef<string | null>(null);

  // Same warn-once-per-offending-occurrence pattern as the sort whitelist above.
  const warnFilterOnce = (occurrence: string, message: string) => {
    if (warnedFilterRef.current === occurrence) {
      return;
    }
    warnedFilterRef.current = occurrence;
    warnInDev(message);
  };

  /**
   * Compiles the URL's chips into the CRUD backend's `filter` strings (spec §7.2). Only
   * fields the resource declares filterable pass — anything else is dropped the way an
   * unknown sort is — and the operator is validated against the field's type, which only
   * the filterable config knows. A chip that compiles to null (empty value, bad arity,
   * unparseable date) contributes nothing (spec §7.4).
   */
  const buildFilters = (state: FilterState): string[] | undefined => {
    const filterable = resource.filterable ?? [];
    if (filterable.length === 0 || Object.keys(state).length === 0) {
      return undefined;
    }
    const byField = new Map(filterable.map((field) => [field.field, field]));
    const compiled: string[] = [];
    for (const [field, chip] of Object.entries(state)) {
      const config = byField.get(field);
      if (!config) {
        warnFilterOnce(
          `${resource.key}:${field}:field`,
          `"${field}" is not a filterable field of "${resource.key}"; dropping the chip from the request.`
        );
        continue;
      }
      if (!OPERATORS_BY_TYPE[config.type].some((operator) => operator.id === chip.op)) {
        warnFilterOnce(
          `${resource.key}:${field}:${chip.op}`,
          `"${chip.op}" is not a valid operator for the ${config.type} filter "${field}" of "${resource.key}"; ` +
            'dropping the chip from the request.'
        );
        continue;
      }
      const segment = compileFilter(field, chip.op, chip.value, dateBound, config.type);
      if (segment) {
        compiled.push(segment);
      }
    }
    return compiled.length > 0 ? compiled : undefined;
  };

  const baseKey = [resource.key, params.orgSlug, params.restaurantId, params.branchId];

  /**
   * Invalidation deliberately uses a SHORTER prefix than the query key. Hand-written hooks
   * that survive a page's migration (e.g. the unpaginated useInventoryCategories the item
   * form and dashboard still call) key on [key, orgSlug, restaurantId] with no branchId, so
   * the 4-element baseKey would not prefix-match them and their lists would go stale after a
   * create. Three elements matches both them and every branch-scoped list.
   */
  const invalidateKey = [resource.key, params.orgSlug, params.restaurantId];

  /**
   * @nestjsx/crud rejects a bare string with 400 "Invalid search param. JSON expected", so the
   * term has to be an $or across the resource's searchFields using $contL (case-insensitive
   * contains). Getting this wrong is silent from the UI's side: the request 400s, React Query
   * yields no rows, and the page just looks empty.
   *
   * The caller normalizes the term before it arrives (see `serverSearch`), so the query key and
   * this JSON always describe the same request.
   */
  const buildSearch = (term: string): string | undefined => {
    const fields = (resource.searchFields ?? []) as string[];
    if (!term || fields.length === 0) {
      return undefined;
    }
    return JSON.stringify({ $or: fields.map((field) => ({ [field]: { $contL: term } })) });
  };

  /**
   * The only form of the search term the REQUEST knows about: trimmed, and empty for a
   * resource that filters in memory. Both the query key and the compiled `s` read this, so a
   * client-side search cannot re-key the query, and a trailing space cannot key a second cache
   * entry for the same JSON.
   */
  const serverSearch = searchMode === 'server' ? filters.debouncedSearch.trim() : '';

  /**
   * The narrowing this resource currently describes — everything the list request carries
   * EXCEPT paging. Memoized on the values it reads, not merely computed: a fresh identity
   * every render would invalidate any query key a view builds from it and put that view into
   * a refetch loop.
   */
  const compiledQuery = useMemo<Omit<ListQuery, 'page' | 'limit'>>(() => {
    // sortableKeys whitelisting applies per entry (spec §5): a column that is not sortable
    // is dropped from the outgoing query, one at a time, while the rest of the precedence
    // order survives — it stays in the URL harmlessly, the same way an unknown filter field
    // does. Warn ONCE per offending occurrence, not per refetch of the same URL state: the
    // occurrence string covers every dropped entry together, so flipping between two
    // different bad sorts still warns, but re-rendering with the same one does not.
    const sortableKeys = options.sortableKeys;
    const dropped: CrudSort[] = [];
    const sort = filters.sort
      .filter((entry) => {
        if (!sortableKeys || sortableKeys.includes(entry.field)) {
          return true;
        }
        dropped.push(entry);
        return false;
      })
      .map((entry) => `${entry.field},${entry.direction}`);

    if (dropped.length > 0) {
      const occurrence = dropped.map((entry) => `${entry.field},${entry.direction}`).join(';');
      if (warnedSortRef.current !== occurrence) {
        warnedSortRef.current = occurrence;
        for (const entry of dropped) {
          warnInDev(
            `"${entry.field}" is not a sortable column of "${resource.key}"; ` +
              'dropping it from the request (the server would 400 on it).'
          );
        }
      }
    }

    return {
      s: buildSearch(serverSearch),
      filter: buildFilters(filters.debouncedFilters),
      sort: sort.length > 0 ? sort : undefined,
    };
    // buildSearch and buildFilters are re-created every render but read only the values
    // listed here, so the memo stays correct while its identity stays stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.debouncedFilters, serverSearch, filters.sort, options.sortableKeys, resource.key, resource.searchFields]);

  const listQuery = useQuery(
    [...baseKey, filters.page, filters.pageSize, serverSearch, filters.debouncedFilters, filters.sort],
    () => resource.api.list(scope, { ...compiledQuery, page: filters.page, limit: pageSize }),
    {
      keepPreviousData: true,
      enabled: options.enabled ?? true,
      staleTime: LIST_STALE_TIME_MS,
      // The portal disables refetch-on-mount app-wide (main.tsx), and `shouldFetchOn` returns
      // false for `refetchOnMount: false` BEFORE it consults staleness — so without this opt-in
      // an entry a mutation had just invalidated would still never refresh on the user's return.
      // Same reasoning, same opt-in as useReferenceOptions.
      refetchOnMount: true,
    }
  );

  /**
   * Everything a mutation can invalidate.
   *
   * The reference lists are config-scale and cached for the session, and ANY resource's
   * mutation can change one — creating a category changes the category filter on a different
   * page. Marking the whole namespace stale is cheap (no network for the entries nothing is
   * observing) and is what useReferenceOptions' refetchOnMount turns into a refresh.
   */
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [REFERENCE_QUERY_PREFIX] }),
      // Mark EVERY list entry this resource owns stale, and refetch the ones on screen
      // (invalidateQueries defaults to refetchType 'active'). This replaced `refetchQueries`,
      // which fetches but does not mark: an entry the save orphaned — a page, sort or filter
      // set the user had visited — kept serving pre-mutation rows when they came back to it,
      // including rows they had just edited or deleted.
      queryClient.invalidateQueries({ queryKey: invalidateKey }),
      // Explicit keys are few and deliberate (dashboard panels), and are still forced even
      // while unmounted — that is what `invalidates` has always meant.
      ...(resource.invalidates ?? []).map((key) => queryClient.refetchQueries([key])),
    ]);

  // Not returned from onSuccess: React Query awaits that promise before the mutation settles,
  // which kept `submitting` true and the modal open until every refresh had landed. The
  // refreshes still start synchronously inside the same tick.
  const refreshAfterMutation = () => {
    void invalidate();
  };

  const create = useMutation((body: TCreate) => resource.api.create(scope, body), {
    onSuccess: refreshAfterMutation,
  });
  // The payload is passed through WHOLE, `id` included. `{ id: string } & TUpdate` is
  // assignable to `TUpdate`, so this needs no cast — and destructuring the id out would strip
  // it from the body, which changes behaviour: the hand-written hooks sent the full object,
  // and the server's update DTO extends its create DTO, so a full object is what it validates.
  const update = useMutation((payload: { id: string } & TUpdate) => resource.api.update(scope, payload.id, payload), {
    onSuccess: refreshAfterMutation,
  });
  const remove = useMutation((id: string) => resource.api.remove(scope, id), { onSuccess: refreshAfterMutation });

  const rows = useMemo(() => {
    const fetched = listQuery.data?.data ?? [];
    if (searchMode !== 'client' || !filters.search) {
      return fetched;
    }
    // `import.meta.env` is Vite's, and this package deliberately does not pull in
    // vite/client types (it must build under the consumer's config, not its own), so the
    // shape is named inline rather than widened to `any`.
    const isDev = Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV);
    if (isDev && (listQuery.data?.total ?? 0) > fetched.length) {
      console.warn(
        `[crud] "${resource.key}" searches client-side but only ${fetched.length} of ${listQuery.data?.total} rows are loaded. ` +
          'Rows past the fetched page will not be found. Switch the resource to search: "server".'
      );
    }
    // Trimmed the same way `serverSearch` is: the server path already drops a trailing space,
    // so a client-mode resource must not treat "rice " as a term that matches nothing.
    const needle = filters.search.trim().toLowerCase();
    const fields = resource.searchFields ?? [];
    return fetched.filter((row) =>
      fields.some((field) =>
        String(row[field] ?? '')
          .toLowerCase()
          .includes(needle)
      )
    );
  }, [listQuery.data, filters.search, searchMode, resource.key, resource.searchFields]);

  return {
    ...filters,
    rows,
    total: listQuery.data?.total ?? 0,
    isLoading: listQuery.isLoading,
    isRefetching: listQuery.isRefetching,
    refetch: listQuery.refetch,
    create,
    update,
    remove,
    submitting: create.isLoading || update.isLoading,
    saveError: resolveErrorMessage(create.error) ?? resolveErrorMessage(update.error),
    deleteError: resolveErrorMessage(remove.error),
    params,
    compiledQuery,
  };
}
